import { useState, useEffect, useCallback, useRef } from 'react'
import {
  figmaApi,
  type ApiFigmaConnection,
  type ApiFigmaImport,
  type ApiFigmaImportDetail,
  type ApiFigmaFileInfo,
} from '../utils/api'

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusColor(s: string) {
  if (s === 'done') return '#22c55e'
  if (s === 'running') return '#3b82f6'
  if (s === 'failed') return '#ef4444'
  return '#9ca3af'
}

function statusLabel(s: string) {
  if (s === 'done') return 'Done'
  if (s === 'running') return 'Running'
  if (s === 'failed') return 'Failed'
  return 'Pending'
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString()
}

// ── Connect PAT modal ─────────────────────────────────────────────────────────

function ConnectModal({ onClose, onConnected }: { onClose: () => void; onConnected: (c: ApiFigmaConnection) => void }) {
  const [pat, setPat] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  async function handleConnect() {
    if (!pat.trim()) { setErr('Enter your Figma Personal Access Token'); return }
    setLoading(true)
    setErr('')
    try {
      const me = await figmaApi.connect(pat.trim())
      onConnected({ connected: true, userHandle: me.handle, userEmail: me.email })
    } catch (e: any) {
      setErr(e.message ?? 'Connection failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 12, padding: 32, width: 480, maxWidth: '90vw' }}>
        <h2 style={{ color: '#fff', marginBottom: 8, fontSize: 18 }}>Connect Figma Account</h2>
        <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
          Generate a Personal Access Token in{' '}
          <strong style={{ color: '#a78bfa' }}>Figma → Settings → Security → Personal access tokens</strong>.
          The token requires read access to files.
        </p>
        <label style={{ color: '#9ca3af', fontSize: 12, display: 'block', marginBottom: 4 }}>Personal Access Token</label>
        <input
          value={pat}
          onChange={e => setPat(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleConnect()}
          type="password"
          placeholder="figd_..."
          style={{ width: '100%', padding: '10px 12px', background: '#111', border: '1px solid #333', borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
        />
        {err && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button
            onClick={handleConnect}
            disabled={loading}
            style={{ flex: 1, padding: '10px 0', background: '#a78bfa', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Connecting…' : 'Connect'}
          </button>
          <button
            onClick={onClose}
            style={{ padding: '10px 16px', background: '#333', border: 'none', borderRadius: 8, color: '#9ca3af', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Import modal ──────────────────────────────────────────────────────────────

function ImportModal({ onClose, onStarted }: { onClose: () => void; onStarted: (id: string) => void }) {
  const [url, setUrl] = useState('')
  const [fileInfo, setFileInfo] = useState<ApiFigmaFileInfo | null>(null)
  const [loadingInfo, setLoadingInfo] = useState(false)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  async function handlePreview() {
    if (!url.trim()) { setErr('Enter Figma URL'); return }
    setLoadingInfo(true)
    setErr('')
    setFileInfo(null)
    try {
      const info = await figmaApi.getFileInfo(url.trim())
      setFileInfo(info)
    } catch (e: any) {
      setErr(e.message ?? 'Failed to fetch file info')
    } finally {
      setLoadingInfo(false)
    }
  }

  async function handleImport() {
    if (!url.trim()) { setErr('Enter Figma URL'); return }
    setLoading(true)
    setErr('')
    try {
      const { importId } = await figmaApi.startImport(url.trim())
      onStarted(importId)
    } catch (e: any) {
      setErr(e.message ?? 'Import failed')
      setLoading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 12, padding: 32, width: 520, maxWidth: '90vw' }}>
        <h2 style={{ color: '#fff', marginBottom: 8, fontSize: 18 }}>Import from Figma</h2>
        <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 20 }}>
          Paste the URL of a Figma file. The importer will extract the design system, components, and generate HTML/CSS for each frame.
        </p>

        <label style={{ color: '#9ca3af', fontSize: 12, display: 'block', marginBottom: 4 }}>Figma URL</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            value={url}
            onChange={e => { setUrl(e.target.value); setFileInfo(null) }}
            onKeyDown={e => e.key === 'Enter' && handlePreview()}
            placeholder="https://www.figma.com/file/..."
            style={{ flex: 1, padding: '10px 12px', background: '#111', border: '1px solid #333', borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none' }}
          />
          <button
            onClick={handlePreview}
            disabled={loadingInfo}
            style={{ padding: '10px 16px', background: '#333', border: '1px solid #444', borderRadius: 8, color: '#9ca3af', cursor: loadingInfo ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
          >
            {loadingInfo ? '…' : 'Preview'}
          </button>
        </div>

        {fileInfo && (
          <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              {fileInfo.thumbnailUrl && (
                <img src={fileInfo.thumbnailUrl} alt="thumbnail" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #333' }} />
              )}
              <div>
                <div style={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}>{fileInfo.fileName}</div>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>{fileInfo.pages.length} page(s): {fileInfo.pages.map(p => p.name).join(', ')}</div>
                <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Modified: {formatDate(fileInfo.lastModified)}</div>
              </div>
            </div>
          </div>
        )}

        {err && <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{err}</p>}

        <div style={{ background: '#111827', border: '1px solid #1d4ed8', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#93c5fd' }}>
          ℹ️ The importer fetches the full document tree, design tokens, styles and generates HTML + CSS for each frame. Visual comparison loop is Phase B.
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={handleImport}
            disabled={loading || !url.trim()}
            style={{ flex: 1, padding: '10px 0', background: '#a78bfa', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: (loading || !url.trim()) ? 'not-allowed' : 'pointer', opacity: (loading || !url.trim()) ? 0.7 : 1 }}
          >
            {loading ? 'Starting…' : 'Start Import'}
          </button>
          <button
            onClick={onClose}
            style={{ padding: '10px 16px', background: '#333', border: 'none', borderRadius: 8, color: '#9ca3af', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Import detail view ────────────────────────────────────────────────────────

function ImportDetail({ importId, onBack }: { importId: string; onBack: () => void }) {
  const [job, setJob] = useState<ApiFigmaImportDetail | null>(null)
  const [tab, setTab] = useState<'overview' | 'tokens' | 'pages'>('overview')
  const [previewPage, setPreviewPage] = useState<number | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await figmaApi.getImport(importId)
      setJob(data)
      if (data.status === 'done' || data.status === 'failed') {
        if (pollingRef.current) clearInterval(pollingRef.current)
      }
    } catch {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [importId])

  useEffect(() => {
    load()
    pollingRef.current = setInterval(load, 2000)
    return () => { if (pollingRef.current) clearInterval(pollingRef.current) }
  }, [load])

  if (!job) return <div style={{ color: '#9ca3af', padding: 40, textAlign: 'center' }}>Loading…</div>

  const ds = job.designSystem
  const pages = job.generatedPages ?? []

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: 13, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        ← Back to imports
      </button>

      <div style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 4 }}>{job.figmaFileName ?? 'Figma Import'}</h2>
            <div style={{ color: '#6b7280', fontSize: 12 }}>{job.figmaUrl}</div>
          </div>
          <span style={{ background: statusColor(job.status) + '22', color: statusColor(job.status), border: `1px solid ${statusColor(job.status)}`, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
            {statusLabel(job.status)}
          </span>
        </div>

        {(job.status === 'pending' || job.status === 'running') && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: '#9ca3af', fontSize: 12 }}>{job.currentStep ?? 'Processing…'}</span>
              <span style={{ color: '#a78bfa', fontSize: 12, fontWeight: 600 }}>{job.progress}%</span>
            </div>
            <div style={{ background: '#111', borderRadius: 4, height: 6, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: 'linear-gradient(90deg, #7c3aed, #a78bfa)', width: `${job.progress}%`, transition: 'width 0.4s ease', borderRadius: 4 }} />
            </div>
          </div>
        )}

        {job.status === 'failed' && job.errorMsg && (
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: 12, marginTop: 16, color: '#fca5a5', fontSize: 12 }}>
            {job.errorMsg}
          </div>
        )}
      </div>

      {job.status === 'done' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {(['overview', 'tokens', 'pages'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: tab === t ? 600 : 400, background: tab === t ? '#7c3aed' : '#1e1e2e', color: tab === t ? '#fff' : '#9ca3af' }}>
                {t === 'overview' ? 'Overview' : t === 'tokens' ? 'Design Tokens' : `Pages (${pages.length})`}
              </button>
            ))}
          </div>

          {tab === 'overview' && ds && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <div style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 12, padding: 20 }}>
                <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 8 }}>Colors</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>{ds.colors.length}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {ds.colors.slice(0, 10).map((c, i) => (
                    <div key={i} title={c.name} style={{ width: 20, height: 20, borderRadius: 4, background: c.value, border: '1px solid #333' }} />
                  ))}
                </div>
              </div>
              <div style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 12, padding: 20 }}>
                <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 8 }}>Typography Styles</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>{ds.typography.length}</div>
                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>Fonts: {ds.fonts.join(', ') || '—'}</div>
              </div>
              <div style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 12, padding: 20 }}>
                <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 8 }}>Generated Pages</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>{pages.length}</div>
                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>Frames: {pages.map(p => p.name).join(', ') || '—'}</div>
              </div>
            </div>
          )}

          {tab === 'tokens' && ds && (
            <div>
              <h3 style={{ color: '#fff', marginBottom: 16, fontSize: 16 }}>Color Tokens</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
                {ds.colors.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#1e1e2e', borderRadius: 8, padding: '8px 12px', border: '1px solid #333' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: c.value, border: '1px solid #444', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#e5e7eb', fontSize: 13 }}>{c.name}</div>
                      {c.group && <div style={{ color: '#6b7280', fontSize: 11 }}>{c.group}</div>}
                    </div>
                    <code style={{ color: '#a78bfa', fontSize: 11, fontFamily: 'monospace' }}>{c.value}</code>
                  </div>
                ))}
              </div>

              <h3 style={{ color: '#fff', marginBottom: 16, fontSize: 16 }}>Typography</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ds.typography.map((t, i) => (
                  <div key={i} style={{ background: '#1e1e2e', borderRadius: 8, padding: '10px 14px', border: '1px solid #333' }}>
                    <div style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                    <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>
                      {t.fontFamily} · {t.fontWeight} · {t.fontSize}px · lh {t.lineHeight}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'pages' && (
            <div>
              {previewPage !== null && pages[previewPage] ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <button onClick={() => setPreviewPage(null)} style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: 13 }}>← Back</button>
                    <h3 style={{ color: '#fff', fontSize: 16, margin: 0 }}>{pages[previewPage].name}</h3>
                  </div>
                  <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                    <a
                      href={`data:text/html;charset=utf-8,${encodeURIComponent(pages[previewPage].fullHtml)}`}
                      download={`${pages[previewPage].slug}.html`}
                      style={{ padding: '8px 14px', background: '#7c3aed', borderRadius: 8, color: '#fff', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}
                    >
                      ⬇ Download HTML
                    </a>
                  </div>
                  <iframe
                    srcDoc={pages[previewPage].fullHtml}
                    title={pages[previewPage].name}
                    style={{ width: '100%', height: 600, border: '1px solid #333', borderRadius: 8, background: '#fff' }}
                  />
                  <h4 style={{ color: '#9ca3af', marginTop: 16, marginBottom: 8, fontSize: 14 }}>Components ({pages[previewPage].components.length})</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {pages[previewPage].components.map((c, i) => (
                      <span key={i} style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 6, padding: '4px 10px', color: '#9ca3af', fontSize: 12 }}>
                        <span style={{ color: '#a78bfa' }}>{c.componentType}</span> · {c.name}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                  {pages.map((p, i) => (
                    <div key={i} onClick={() => setPreviewPage(i)} style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 12, padding: 16, cursor: 'pointer', transition: 'border-color 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.borderColor = '#7c3aed')}
                      onMouseLeave={e => (e.currentTarget.style.borderColor = '#333')}>
                      <div style={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{p.components.length} components</div>
                      <div style={{ color: '#a78bfa', fontSize: 12, marginTop: 8 }}>Click to preview →</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function FigmaPanel() {
  const [connection, setConnection] = useState<ApiFigmaConnection | null>(null)
  const [imports, setImports] = useState<ApiFigmaImport[]>([])
  const [loadingConn, setLoadingConn] = useState(true)
  const [showConnect, setShowConnect] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    try {
      const [conn, list] = await Promise.all([figmaApi.getConnection(), figmaApi.listImports()])
      setConnection(conn)
      setImports(list)
    } catch {
      // silent
    } finally {
      setLoadingConn(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  async function handleDisconnect() {
    if (!confirm('Disconnect Figma account?')) return
    await figmaApi.disconnect()
    setConnection(null)
  }

  async function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    if (!confirm('Delete this import?')) return
    await figmaApi.deleteImport(id)
    setImports(prev => prev.filter(i => i.id !== id))
  }

  if (loadingConn) return <div style={{ color: '#9ca3af', padding: 40, textAlign: 'center' }}>Loading…</div>

  if (detailId) return <ImportDetail importId={detailId} onBack={() => { setDetailId(null); loadData() }} />

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 28 }}>
        <div>
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Figma Import</h1>
          <p style={{ color: '#6b7280', fontSize: 14 }}>Import Figma designs — extract design system, components, and generate HTML/CSS</p>
        </div>
        {connection?.connected && (
          <button
            onClick={() => setShowImport(true)}
            style={{ padding: '10px 20px', background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          >
            + New Import
          </button>
        )}
      </div>

      {/* Connection card */}
      <div style={{ background: '#1e1e2e', border: `1px solid ${connection?.connected ? '#7c3aed' : '#333'}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: connection?.connected ? '#7c3aed22' : '#111', border: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
              🎨
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>
                {connection?.connected ? (connection.userHandle ?? connection.userEmail ?? 'Connected') : 'Not connected'}
              </div>
              <div style={{ color: '#6b7280', fontSize: 12 }}>
                {connection?.connected
                  ? (connection.userEmail ?? 'Figma account connected')
                  : 'Add your Figma Personal Access Token'}
              </div>
            </div>
          </div>
          {connection?.connected ? (
            <button
              onClick={handleDisconnect}
              style={{ padding: '8px 14px', background: 'none', border: '1px solid #444', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 13 }}
            >
              Disconnect
            </button>
          ) : (
            <button
              onClick={() => setShowConnect(true)}
              style={{ padding: '8px 18px', background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
            >
              Connect Figma
            </button>
          )}
        </div>
      </div>

      {/* Imports list */}
      {connection?.connected && (
        <>
          <h2 style={{ color: '#9ca3af', fontSize: 14, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Import History
          </h2>
          {imports.length === 0 ? (
            <div style={{ background: '#1e1e2e', border: '1px dashed #333', borderRadius: 12, padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎨</div>
              <div style={{ color: '#6b7280', fontSize: 14 }}>No imports yet</div>
              <div style={{ color: '#4b5563', fontSize: 12, marginTop: 4 }}>Paste a Figma URL to get started</div>
              <button
                onClick={() => setShowImport(true)}
                style={{ marginTop: 16, padding: '10px 24px', background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
              >
                Import Figma File
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {imports.map(imp => (
                <div
                  key={imp.id}
                  onClick={() => setDetailId(imp.id)}
                  style={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 10, padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, transition: 'border-color 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = '#7c3aed')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = '#333')}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {imp.figmaFileName ?? 'Figma Import'}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {imp.figmaUrl}
                    </div>
                    {imp.currentStep && imp.status !== 'done' && (
                      <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 4 }}>{imp.currentStep}</div>
                    )}
                  </div>

                  {(imp.status === 'pending' || imp.status === 'running') && (
                    <div style={{ width: 80 }}>
                      <div style={{ background: '#111', borderRadius: 3, height: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#7c3aed', width: `${imp.progress}%`, transition: 'width 0.4s' }} />
                      </div>
                      <div style={{ color: '#6b7280', fontSize: 10, textAlign: 'right', marginTop: 2 }}>{imp.progress}%</div>
                    </div>
                  )}

                  <span style={{ background: statusColor(imp.status) + '22', color: statusColor(imp.status), border: `1px solid ${statusColor(imp.status)}`, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {statusLabel(imp.status)}
                  </span>

                  <div style={{ color: '#6b7280', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {formatDate(imp.createdAt)}
                  </div>

                  <button
                    onClick={e => handleDelete(imp.id, e)}
                    style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 4px' }}
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!connection?.connected && (
        <div style={{ background: '#1e1e2e', border: '1px dashed #333', borderRadius: 12, padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎨</div>
          <div style={{ color: '#e5e7eb', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Connect Figma to get started</div>
          <div style={{ color: '#6b7280', fontSize: 14, maxWidth: 400, margin: '0 auto 20px' }}>
            Add your Figma Personal Access Token to import designs, extract design systems, and generate production-ready HTML/CSS.
          </div>
          <button
            onClick={() => setShowConnect(true)}
            style={{ padding: '10px 24px', background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          >
            Connect Figma Account
          </button>
        </div>
      )}

      {showConnect && (
        <ConnectModal
          onClose={() => setShowConnect(false)}
          onConnected={c => { setConnection(c); setShowConnect(false) }}
        />
      )}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onStarted={id => { setShowImport(false); setDetailId(id) }}
        />
      )}
    </div>
  )
}
